import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, XCircle, PauseCircle, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Modal, StatusBadge, Field, inputCls, thCls, tdCls } from "./ui";

const STATUS_TABS = ["all", "pending", "published", "rejected", "suspended"];

const EMPTY_FORM = {
  name: "", category: "", description: "", price: "", event_date: "", phone: "", email: "", website: "",
  address: { line1: "", city: "", state: "", postal: "", country: "US" },
  images: "", languages: "", hours: "", actions: {},
  status: "published", featured: false, sponsored: false, verified: false, owner_email: "",
  details: "", highlights: "", facilities: "", service_options: "",
};

function toForm(l) {
  return {
    ...EMPTY_FORM,
    ...l,
    images: (l.images || []).join("\n"),
    languages: (l.languages || []).join(", "),
    hours: Object.entries(l.hours || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
    actions: l.actions || {},
    address: { line1: "", city: "", state: "", postal: "", country: "US", ...(l.address || {}) },
    details: Object.entries(l.details || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
    highlights: (l.highlights || []).join(", "),
    facilities: (l.facilities || []).join(", "),
    service_options: (l.service_options || []).join(", "),
  };
}

export default function Listings() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [listings, setListings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const p = {};
    if (tab !== "all") p.status = tab;
    if (q) p.q = q;
    api.get("/admin/listings", { params: p }).then((r) => setListings(r.data || [])).catch(() => setListings([]));
  };

  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/admin/listings/${id}`, body);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this listing permanently?")) return;
    await api.delete(`/admin/listings/${id}`).catch(() => {});
    toast.success("Listing deleted.");
    load();
  };

  const openNew = () => { setForm({ ...EMPTY_FORM, category: categories[0]?.slug || "" }); setEditing("new"); };
  const openEdit = (l) => { setForm(toForm(l)); setEditing(l.id); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const hours = {};
    form.hours.split("\n").forEach((line) => {
      const [d, ...rest] = line.split(":");
      if (d && rest.length) hours[d.trim()] = rest.join(":").trim();
    });
    const payload = {
      ...form,
      hours,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      actions: Object.fromEntries(Object.entries(form.actions || {}).filter(([, v]) => v)),
      details: Object.fromEntries((form.details || "").split("\n").map((l) => l.split(/:(.+)/)).filter((p) => p.length >= 2 && p[0] && p[0].trim()).map((p) => [p[0].trim(), (p[1] || "").trim()])),
      highlights: (form.highlights || "").split(",").map((s) => s.trim()).filter(Boolean),
      facilities: (form.facilities || "").split(",").map((s) => s.trim()).filter(Boolean),
      service_options: (form.service_options || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") {
        const { data } = await api.post("/admin/listings", payload);
        await api.patch(`/admin/listings/${data.id}`, { status: form.status, featured: form.featured, sponsored: form.sponsored, verified: form.verified });
      } else {
        await api.put(`/admin/listings/${editing}`, payload);
        await api.patch(`/admin/listings/${editing}`, { status: form.status, featured: form.featured, sponsored: form.sponsored, verified: form.verified });
      }
      toast.success(editing === "new" ? "Listing created." : "Listing updated.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  return (
    <div data-testid="admin-listings">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex bg-white rounded-xl border border-[#E6DED3] p-1">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              onClick={() => setTab(s)}
              data-testid={`listings-tab-${s}`}
              className={`px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors ${tab === s ? "bg-ruby text-cream" : "text-clay hover:text-ink"}`}
            >
              {s}
            </button>
          ))}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search name…"
          className="px-4 py-2.5 rounded-xl bg-white border border-[#E6DED3] text-sm outline-none focus:border-gold"
          data-testid="listings-search-input"
        />
        <button onClick={load} className="text-xs font-semibold text-ruby" data-testid="listings-search-btn">Search</button>
        <button onClick={openNew} className="ml-auto inline-flex items-center gap-2 bg-ruby text-cream text-sm font-semibold px-5 py-2.5 rounded-xl" data-testid="new-listing-btn">
          <Plus className="w-4 h-4" /> New listing
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[820px]" data-testid="listings-table">
          <thead className="border-b border-sand">
            <tr>
              <th className={thCls}>Listing</th><th className={thCls}>Category</th><th className={thCls}>Location</th>
              <th className={thCls}>Status</th><th className={thCls}>Labels</th><th className={thCls}>Views</th><th className={thCls}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sand">
            {(listings || []).map((l) => (
              <tr key={l.id} data-testid={`listing-row-${l.id}`}>
                <td className={tdCls}>
                  <p className="font-semibold">{l.name}</p>
                  <p className="text-xs text-stone2">{l.submitted_by?.email || l.email || ""}</p>
                </td>
                <td className={tdCls}>{l.category_label}</td>
                <td className={tdCls}>{[l.address?.city, l.address?.state].filter(Boolean).join(", ") || "—"}</td>
                <td className={tdCls}><StatusBadge status={l.status} /></td>
                <td className={tdCls}>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => patch(l.id, { featured: !l.featured }, "Updated labels.")}
                      data-testid={`toggle-featured-${l.id}`}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${l.featured ? "bg-gold text-ink border-gold" : "text-stone2 border-[#E6DED3]"}`}
                    >Featured</button>
                    <button
                      onClick={() => patch(l.id, { sponsored: !l.sponsored }, "Updated labels.")}
                      data-testid={`toggle-sponsored-${l.id}`}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${l.sponsored ? "bg-ruby text-white border-ruby" : "text-stone2 border-[#E6DED3]"}`}
                    >Sponsored</button>
                  </div>
                </td>
                <td className={tdCls}>{l.views || 0}</td>
                <td className={tdCls}>
                  <div className="flex items-center gap-1">
                    {l.status === "pending" && (
                      <>
                        <button title="Approve & publish" onClick={() => patch(l.id, { status: "published" }, "Listing published.")} className="p-2 text-jade hover:bg-jade-light rounded-lg" data-testid={`approve-${l.id}`}><Check className="w-4 h-4" /></button>
                        <button title="Reject" onClick={() => patch(l.id, { status: "rejected" }, "Listing rejected.")} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`reject-${l.id}`}><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                    {l.status === "published" && (
                      <button title="Suspend" onClick={() => patch(l.id, { status: "suspended" }, "Listing suspended.")} className="p-2 text-gold-dark hover:bg-gold-light rounded-lg" data-testid={`suspend-${l.id}`}><PauseCircle className="w-4 h-4" /></button>
                    )}
                    {(l.status === "suspended" || l.status === "rejected") && (
                      <button title="Publish" onClick={() => patch(l.id, { status: "published" }, "Listing published.")} className="p-2 text-jade hover:bg-jade-light rounded-lg" data-testid={`publish-${l.id}`}><Globe className="w-4 h-4" /></button>
                    )}
                    <button title="Edit" onClick={() => openEdit(l)} className="p-2 text-clay hover:bg-sand rounded-lg" data-testid={`edit-${l.id}`}><Pencil className="w-4 h-4" /></button>
                    <button title="Delete" onClick={() => remove(l.id)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {listings && listings.length === 0 && <p className="px-5 py-10 text-sm text-stone2 text-center">No listings in this view.</p>}
      </div>

      {editing && (
        <Modal title={editing === "new" ? "New listing" : `Edit — ${form.name}`} onClose={() => setEditing(null)} wide>
          <form onSubmit={save} className="space-y-4" data-testid="listing-edit-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name *"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="edit-name-input" /></Field>
              <Field label="Category *">
                <select required value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="edit-category-select">
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="edit-description-input" /></Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Price / salary"><input value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="edit-price-input" /></Field>
              <Field label="Event date"><input type="date" value={form.event_date || ""} onChange={(e) => set("event_date", e.target.value)} className={inputCls} data-testid="edit-event-date-input" /></Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="edit-status-select">
                  {["pending", "published", "rejected", "suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="edit-phone-input" /></Field>
              <Field label="Public email"><input value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="edit-email-input" /></Field>
              <Field label="Website"><input value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} data-testid="edit-website-input" /></Field>
              <Field label="Owner email (links listing to an owner account)"><input value={form.owner_email || ""} onChange={(e) => set("owner_email", e.target.value)} className={inputCls} placeholder="owner@business.com" data-testid="edit-owner-email-input" /></Field>
              <Field label="Languages (comma)"><input value={form.languages} onChange={(e) => set("languages", e.target.value)} className={inputCls} data-testid="edit-languages-input" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Street"><input value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} className={inputCls} data-testid="edit-address-input" /></Field>
              <Field label="City"><input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} className={inputCls} data-testid="edit-city-input" /></Field>
              <Field label="State / Region"><input value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} className={inputCls} data-testid="edit-state-input" /></Field>
              <Field label="Postal code"><input value={form.address.postal} onChange={(e) => setAddr("postal", e.target.value)} className={inputCls} data-testid="edit-postal-input" /></Field>
            </div>
            <Field label="Photo URLs (one per line)"><textarea rows={2} value={form.images} onChange={(e) => set("images", e.target.value)} className={inputCls} data-testid="edit-images-input" /></Field>
            <Field label="Hours (one per line: Mon-Fri: 9-6)"><textarea rows={2} value={form.hours} onChange={(e) => set("hours", e.target.value)} className={inputCls} data-testid="edit-hours-input" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              {[["order_online", "Order Online URL"], ["menu", "Menu URL"], ["book", "Booking URL"], ["reservation", "Reservation URL"], ["quote", "Quote URL"], ["shop", "Shop URL"]].map(([k, l]) => (
                <Field key={k} label={l}>
                  <input value={form.actions?.[k] || ""} onChange={(e) => setForm((f) => ({ ...f, actions: { ...f.actions, [k]: e.target.value } }))} className={inputCls} data-testid={`edit-action-${k}`} />
                </Field>
              ))}
            </div>
            <div className="border border-sand rounded-xl p-4 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone2">Detail page extras (shown on the listing page)</p>
              <Field label="Details (one per line: Cuisine: Burmese, Tea House)"><textarea rows={3} value={form.details} onChange={(e) => set("details", e.target.value)} className={inputCls} data-testid="edit-details-input" /></Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Highlights (comma)"><input value={form.highlights} onChange={(e) => set("highlights", e.target.value)} className={inputCls} data-testid="edit-highlights-input" /></Field>
                <Field label="Facilities (comma)"><input value={form.facilities} onChange={(e) => set("facilities", e.target.value)} className={inputCls} data-testid="edit-facilities-input" /></Field>
                <Field label="Service options (comma)"><input value={form.service_options} onChange={(e) => set("service_options", e.target.value)} className={inputCls} data-testid="edit-service-options-input" /></Field>
              </div>
            </div>
            <div className="flex flex-wrap gap-5 pt-1">
              {[["featured", "Featured"], ["sponsored", "Sponsored"], ["verified", "Verified"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="w-4 h-4 accent-[#8B0018]" data-testid={`edit-flag-${k}`} />
                  {l}
                </label>
              ))}
            </div>
            <button type="submit" disabled={busy} className="w-full bg-ruby hover:bg-ruby-dark text-cream font-bold py-3.5 rounded-xl disabled:opacity-60" data-testid="listing-save-btn">
              {busy ? "Saving…" : "Save listing"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
